import React, { useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Code2,
  Eye,
  FileUp,
  Mail,
  Search,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { VendorState } from '../../../redux/vendor/rootReducer';
import { EmailTemplate } from '../../../modules/timeline/components/timeline-types';
import { EmailTemplatesService } from '../../../modules/timeline/services/email-templates.service';

const PLACEHOLDER_KEYS = [
  'COMPANY_NAME', 'COMPANY_LOGO', 'HEADER_TITLE', 'HEADER_SUBTITLE',
  'WELCOME_MESSAGE', 'PRODUCT_SECTION_TITLE', 'PRODUCTS', 'VOUCHER_SECTION',
  'FOOTER_TEXT', 'COPYRIGHT_TEXT', 'PRIMARY_COLOR', 'SECONDARY_COLOR',
] as const;

const PLACEHOLDER_LABEL_KEY: Record<string, string> = {
  COMPANY_NAME: 'placeholderCompanyName',
  COMPANY_LOGO: 'placeholderCompanyLogo',
  HEADER_TITLE: 'placeholderHeaderTitle',
  HEADER_SUBTITLE: 'placeholderHeaderSubtitle',
  WELCOME_MESSAGE: 'placeholderWelcomeMessage',
  PRODUCT_SECTION_TITLE: 'placeholderProductSectionTitle',
  PRODUCTS: 'placeholderProducts',
  VOUCHER_SECTION: 'placeholderVoucherSection',
  FOOTER_TEXT: 'placeholderFooterText',
  COPYRIGHT_TEXT: 'placeholderCopyrightText',
  PRIMARY_COLOR: 'placeholderPrimaryColor',
  SECONDARY_COLOR: 'placeholderSecondaryColor',
};

export const EmailTemplatesPage = () => {
  const { t } = useTranslation();
  const { theme } = useSelector((state: VendorState) => state.settings);

  const [templates, setTemplates] = useState<EmailTemplate[]>(EmailTemplatesService.getAllTemplates());
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate>(EmailTemplatesService.getDefaultTemplate());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadStep, setUploadStep] = useState<'upload' | 'name'>('upload');
  const [newTemplate, setNewTemplate] = useState({ name: '', htmlFile: null as File | null });
  const [dragActive, setDragActive] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [showPlaceholders, setShowPlaceholders] = useState(false);

  const handleTemplateSelect = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    localStorage.setItem('selectedEmailTemplate', template.id);
  };

  const handlePreview = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setPreviewOpen(true);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'text/html') {
      setNewTemplate(prev => ({ ...prev, htmlFile: file }));
      setUploadStep('name');
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type === 'text/html') {
      setNewTemplate(prev => ({ ...prev, htmlFile: file }));
      setUploadStep('name');
    }
  };

  const handleUploadTemplate = async () => {
    if (newTemplate.name && newTemplate.htmlFile) {
      try {
        const htmlContent = await newTemplate.htmlFile.text();
        EmailTemplatesService.addCustomTemplate({
          name: newTemplate.name,
          description: `Custom template: ${newTemplate.name}`,
          htmlContent: htmlContent,
          placeholders: extractPlaceholders(htmlContent)
        });
        setTemplates(EmailTemplatesService.getAllTemplates());
        setNewTemplate({ name: '', htmlFile: null });
        setUploadStep('upload');
        setUploadOpen(false);
      } catch (error) {
        console.error('Error reading file:', error);
      }
    }
  };

  const handleUploadOpenChange = (open: boolean) => {
    setUploadOpen(open);
    if (!open) {
      setUploadStep('upload');
      setNewTemplate({ name: '', htmlFile: null });
      setDragActive(false);
    }
  };

  const handleDeleteTemplate = (templateId: string) => {
    if (EmailTemplatesService.deleteCustomTemplate(templateId)) {
      setTemplates(EmailTemplatesService.getAllTemplates());
      if (selectedTemplate.id === templateId) {
        setSelectedTemplate(EmailTemplatesService.getDefaultTemplate());
      }
    }
  };

  const extractPlaceholders = (html: string): string[] => {
    const matches = html.match(/\{\{[^}]+\}\}/g);
    return matches ? [...new Set(matches)] : [];
  };

  const getPreviewHtml = (template: EmailTemplate) => {
    return EmailTemplatesService.replaceTemplatePlaceholders(
      template,
      {
        companyName: theme?.vendorName || 'Demo Company',
        companyLogo: theme?.logo || '',
        headerTitle: 'Sample Email Campaign',
        headerSubtitle: 'This is how your email will look',
        footerText: 'Thank you for choosing our products!',
        primaryColor: theme?.primaryColor || '#3a8589',
        secondaryColor: theme?.secondaryColor || '#00575b'
      },
      [{
        productName: 'Sample Product',
        productBrand: 'Demo Brand',
        thumbnailPath: '',
        usageCount: 5,
        visitCount: 12
      }],
      false
    );
  };

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <div className="mx-auto max-w-3xl">
        {/* Header: Search + Upload */}
        <div className="mb-5 flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="h-9 ps-8"
              placeholder={t('PAGES.EMAIL_TEMPLATES.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <Popover open={uploadOpen} onOpenChange={handleUploadOpenChange}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-9">
                <Upload className="size-4" />
                {t('PAGES.EMAIL_TEMPLATES.uploadTemplate')}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[260px] p-3">
              {uploadStep === 'upload' ? (
                <div className="flex flex-col gap-2">
                  <input accept=".html" className="hidden" id="html-file-upload" type="file" onChange={handleFileUpload} />
                  <label htmlFor="html-file-upload">
                    <div
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      className={cn(
                        'flex min-h-[100px] cursor-pointer flex-col items-center justify-center gap-1.5 border-2 border-dashed p-4 transition-colors',
                        dragActive ? 'border-foreground bg-accent' : 'border-input hover:border-foreground/40 hover:bg-accent',
                      )}
                    >
                      <FileUp className="size-7 text-muted-foreground/40" />
                      <p className="text-center text-xs font-medium">Drop HTML file</p>
                      <p className="text-center text-[11px] text-muted-foreground">or click to browse</p>
                    </div>
                  </label>
                  <p className="px-0.5 text-center text-[10px] text-muted-foreground">
                    {t('PAGES.EMAIL_TEMPLATES.uploadHint')}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-2.5">
                  <div className="flex items-center gap-2 border bg-muted p-2">
                    <Code2 className="size-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate text-xs font-medium">{newTemplate.htmlFile?.name}</span>
                    <button
                      type="button"
                      onClick={() => { setNewTemplate({ name: '', htmlFile: null }); setUploadStep('upload'); }}
                      aria-label={t('PAGES.EMAIL_TEMPLATES.close')}
                      className="text-muted-foreground transition-colors hover:text-foreground"
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                  <Input
                    placeholder={t('PAGES.EMAIL_TEMPLATES.templateName')}
                    value={newTemplate.name}
                    onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter' && newTemplate.name) handleUploadTemplate(); }}
                    autoFocus
                  />
                  <Button className="w-full" onClick={handleUploadTemplate} disabled={!newTemplate.name}>
                    {t('PAGES.EMAIL_TEMPLATES.saveTemplate')}
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        </div>

        {/* Templates grid */}
        <div className="grid gap-4 sm:grid-cols-2">
          {filteredTemplates.map((template) => {
            const isSelected = selectedTemplate.id === template.id;

            return (
              <button
                type="button"
                key={template.id}
                onClick={() => handleTemplateSelect(template)}
                aria-pressed={isSelected}
                className={cn(
                  'group/template relative flex flex-col overflow-hidden rounded-xl border bg-card text-start transition-all',
                  isSelected ? 'border-[1.5px] border-foreground' : 'hover:border-foreground/40',
                )}
              >
                {/* Preview area */}
                <div className="relative flex h-[100px] items-center justify-center border-b bg-muted/50">
                  {isSelected ? (
                    <Check className="size-9 animate-in text-muted-foreground/40 zoom-in-50" />
                  ) : (
                    <Mail className="size-9 text-muted-foreground/25" />
                  )}

                  <Badge
                    variant="outline"
                    className="absolute start-2 top-2 h-5 bg-background text-[10px] text-muted-foreground"
                  >
                    {template.isDefault ? t('PAGES.EMAIL_TEMPLATES.badgeDefault') : t('PAGES.EMAIL_TEMPLATES.badgeCustom')}
                  </Badge>

                  {/* Hover actions */}
                  <div className="absolute end-2 top-2 flex gap-1 opacity-0 transition-opacity group-hover/template:opacity-100">
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); handlePreview(template); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handlePreview(template); } }}
                      aria-label={t('PAGES.EMAIL_TEMPLATES.previewTitle')}
                      className="flex size-7 cursor-pointer items-center justify-center border bg-background text-muted-foreground transition-colors hover:bg-accent"
                    >
                      <Eye className="size-3.5" />
                    </span>
                    {template.isCustom && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(template.id); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleDeleteTemplate(template.id); } }}
                        aria-label={t('PAGES.EMAIL_TEMPLATES.badgeCustom')}
                        className="flex size-7 cursor-pointer items-center justify-center border bg-background text-muted-foreground transition-colors hover:border-destructive/40 hover:bg-destructive/5 hover:text-destructive"
                      >
                        <Trash2 className="size-3.5" />
                      </span>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 p-4">
                  <p className="mb-1 text-sm font-medium">{template.name}</p>
                  <p className="mb-3 text-xs leading-relaxed text-muted-foreground">{template.description}</p>

                  <div className="flex flex-wrap gap-1">
                    {template.placeholders.slice(0, 3).map((placeholder, index) => (
                      <Badge key={index} variant="outline" className="h-[18px] font-mono text-[10px] text-muted-foreground">
                        {placeholder.replace(/[{}]/g, '').substring(0, 10)}
                      </Badge>
                    ))}
                    {template.placeholders.length > 3 && (
                      <Badge variant="outline" className="h-[18px] text-[10px] text-muted-foreground">
                        +{template.placeholders.length - 3}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Selected indicator bar */}
                {isSelected && <div className="h-0.5 animate-in bg-foreground fade-in" />}
              </button>
            );
          })}
        </div>

        {/* Empty state */}
        {filteredTemplates.length === 0 && (
          <div className="flex min-h-[30vh] flex-col items-center justify-center gap-3">
            <Mail className="size-12 text-muted-foreground/25" />
            <p className="text-sm text-muted-foreground">{t('PAGES.EMAIL_TEMPLATES.noTemplates')}</p>
          </div>
        )}

        {/* Available placeholders */}
        <div className="mt-6">
          <button
            type="button"
            onClick={() => setShowPlaceholders(!showPlaceholders)}
            aria-expanded={showPlaceholders}
            className="flex w-full items-center justify-between rounded-xl border bg-card p-4 text-start transition-colors hover:bg-accent"
          >
            <span className="flex items-center gap-3">
              <Code2 className="size-4.5 text-muted-foreground" />
              <span>
                <span className="block text-sm font-medium">{t('PAGES.EMAIL_TEMPLATES.availablePlaceholders')}</span>
                <span className="block text-xs text-muted-foreground">{t('PAGES.EMAIL_TEMPLATES.placeholdersDescription')}</span>
              </span>
            </span>
            {showPlaceholders ? (
              <ChevronUp className="size-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="size-4 text-muted-foreground" />
            )}
          </button>

          {showPlaceholders && (
            <div className="border border-t-0 bg-card p-4">
              <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
                {PLACEHOLDER_KEYS.map((key) => {
                  const token = `{{${key}}}`;
                  return (
                    <button
                      type="button"
                      key={key}
                      onClick={() => { navigator.clipboard.writeText(token); }}
                      className="border bg-muted/40 p-2.5 text-start transition-colors hover:border-foreground/40 hover:bg-accent"
                    >
                      <span className="mb-0.5 block font-mono text-[11px] font-medium">{token}</span>
                      <span className="block text-[10px] leading-snug text-muted-foreground">
                        {t(`PAGES.EMAIL_TEMPLATES.${PLACEHOLDER_LABEL_KEY[key]}`)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={(open) => !open && setPreviewOpen(false)}>
        <DialogContent className="flex max-h-[90vh] flex-col gap-0 p-0 sm:max-w-4xl">
          <DialogHeader className="border-b p-5 text-start">
            <div className="flex items-center gap-3">
              <span className="flex size-9 items-center justify-center bg-muted text-muted-foreground">
                <Eye className="size-5" />
              </span>
              <div>
                <DialogTitle className="text-base">{t('PAGES.EMAIL_TEMPLATES.previewTitle')}</DialogTitle>
                <DialogDescription>{selectedTemplate.name}</DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="h-[70vh] min-h-0 flex-1">
            <iframe
              srcDoc={getPreviewHtml(selectedTemplate)}
              className="block size-full border-0"
              title="Email Preview"
            />
          </div>

          <DialogFooter className="mx-0 mb-0 border-t p-5">
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>
              {t('PAGES.EMAIL_TEMPLATES.close')}
            </Button>
            <Button onClick={() => { handleTemplateSelect(selectedTemplate); setPreviewOpen(false); }}>
              {t('PAGES.EMAIL_TEMPLATES.useTemplate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EmailTemplatesPage;
